<script setup>
import { useInfiniteScroll } from '@vueuse/core'
import CustomerStoreProductCard from '@/store/components/CustomerStoreProductCard.vue'
import { useCustomerCartState } from '@/store/composables/useCustomerCartState'

const api = useApi()
const cart = useCustomerCartState()
const products = ref([])
const currentPage = ref(1)
const currentPageSize = ref(9)
const totalProductCount = ref(0)
const currentProductCount = ref(0)
const scrollComponent = ref(null)
const loading = ref(false)
const { t } = useI18n()

onMounted(async () => {
  const productsList = await getProducts()
  if (productsList.length)
    currentProductCount.value = productsList.length
  products.value = productsList

  cart.value = await getCartData()
})

async function getCartData() {
  const apiParams = { expand: ['items'] }
  if (cart.value && cart.value.cart_uuid) {
    apiParams.headers = {
      'Cart-UUID': cart.value.cart_uuid,
    }
  }

  const currentCart = await api.getCurrentOrder(apiParams)
  return currentCart
}

async function getProducts() {
  if (totalProductCount.value && currentProductCount.value === totalProductCount.value) {
    return
  }
  else if (currentProductCount.value < totalProductCount.value) {
    loading.value = true
    currentPage.value++
  }
  const productsResponse = await api.listProducts({
    expand: ['images'],
    queryParameters: {
      page: currentPage.value,
      page_size: currentPageSize.value,
    },
  })
  totalProductCount.value = productsResponse.count
  loading.value = false
  return productsResponse.results
}

async function loadMoreProducts() {
  if (totalProductCount.value && currentProductCount.value >= totalProductCount.value)
    return

  const productsList = await getProducts()
  if (productsList.length)
    currentProductCount.value += productsList.length
  products.value = products.value.concat(productsList)
}

async function addToCart(productId) {
  const product = products.value.find(p => p.id === productId)
  cart.value.push(product)
}

function handleInfiniteScroll() {
  if (loading.value || currentProductCount.value >= totalProductCount.value)
    return
  loadMoreProducts()
}

useInfiniteScroll(scrollComponent, handleInfiniteScroll)
</script>

<template>
  <section class="mx-4 pb-2 sm:(border-1 border-surface-600 rounded-1 bg-white px-4 pt-4) md:(px-6 pb-2 pt-6)">
    <header class="mb-6 flex flex-col-reverse gap-6 sm:(flex-row items-center justify-between)">
      <h1 class="text-24px text-secondary-400 font-500">
        {{ t('All Products') }}
      </h1>
      <CustomerStoreHeaderButtons />
    </header>
    <div class="relative flex flex-col sm:(flex-row flex-wrap justify-start gap-x-4)">
      <CustomerStoreProductCard
        v-for="product in products"
        :key="product?.id"
        class="mb-4 w-full overflow-hidden md:w-[calc((100%/3)-11px)] sm:max-md:w-[calc(50%-16px)]"
        :product="product"
        @add-to-cart="addToCart"
      >
        <template #actions>
          <div class="w-full flex justify-between">
            <BaseLink
              class="w-full flex cursor-pointer items-center justify-center rounded-40px bg-surface-400 px-16px py-8px text-16px text-text-400 font-600 active:bg-surface-600 focus:bg-surface-600 hover:bg-surface-600"
              :url="`/store/${product.id}`"
            >
              {{ t('View Product') }}
            </BaseLink>
          </div>
        </template>
      </CustomerStoreProductCard>
      <div
        ref="scrollComponent"
        class="w-full flex justify-center"
      >
        <DSSpinner v-if="loading" />
      </div>
    </div>
  </section>
</template>

<i18n>
{
  "en-us": {
    "All Products": "All Products",
    "Cart": "Cart",
    "Orders": "Orders",
    "Store": "Store",
    "View Product": "View Product"
  }
}
</i18n>

<i18n locale="de" lang="json">
{
  "All Products": "Alle Produkte",
  "Cart": "Warenkorb",
  "Orders": "Bestellungen",
  "Store": "Shop",
  "View Product": "Produkt anzeigen"
}
</i18n>

<i18n locale="es" lang="json">
{
  "All Products": "Todos los productos",
  "Cart": "Carrito",
  "Orders": "Pedidos",
  "Store": "Tienda",
  "View Product": "Ver producto"
}
</i18n>

<i18n locale="fr" lang="json">
{
  "All Products": "Tous les produits",
  "Cart": "Panier",
  "Orders": "Commandes",
  "Store": "Boutique",
  "View Product": "Voir le produit"
}
</i18n>
